import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ScanLine, Upload, Trash2, Download, Image, User, Search } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminScans = () => {
  const { t } = useLanguage();
  const [patients, setPatients] = useState([]);
  const [allScans, setAllScans] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const [scanType, setScanType] = useState('xray');
  const [searchTerm, setSearchTerm] = useState('');

  const getToken = () => localStorage.getItem('adminToken');

  useEffect(() => {
    fetchPatients();
    fetchAllScans();
  }, []);

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API}/admin/patients`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setPatients(res.data);
    } catch (e) { toast.error('Failed to load patients'); }
  };

  const fetchAllScans = async () => {
    try {
      const res = await axios.get(`${API}/admin/all-scans`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setAllScans(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedPatient) { toast.error('Please select a patient first'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('patient_id', selectedPatient);
      formData.append('description', description);
      formData.append('scan_type', scanType);
      await axios.post(`${API}/scans/upload`, formData, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } });
      toast.success('Scan uploaded!');
      setDescription('');
      fetchAllScans();
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scan?')) return;
    try {
      await axios.delete(`${API}/scans/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Deleted');
      setAllScans(allScans.filter(s => s.id !== id));
    } catch (e) { toast.error('Failed'); }
  };

  const filtered = allScans.filter(s => {
    const matchesSearch = !searchTerm || 
      (s.patient_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.scan_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.filename || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-scans-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.patientXrays}</h1>
        <p className="text-[#6B7280]">{t.patientXraysSubtitle}</p>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
        <h2 className="text-lg font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.uploadNewScan}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="upload-patient-select">
            <option value="">Select Patient *</option>
            {patients.map(p => <option key={p.id} value={p.id}>{p.name} - {p.email}</option>)}
          </select>
          <select value={scanType} onChange={e => setScanType(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20">
            <option value="xray">X-Ray</option>
            <option value="panoramic">Panoramic</option>
            <option value="cbct">CBCT</option>
            <option value="other">Other</option>
          </select>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" />
          <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#0F4C5C]/30 rounded-lg cursor-pointer hover:bg-[#0F4C5C]/5 transition-colors" data-testid="scan-upload-btn">
            <Upload className="w-5 h-5 text-[#0F4C5C]" />
            <span className="text-sm font-medium text-[#0F4C5C]">{uploading ? t.uploading : t.upload}</span>
            <input type="file" accept="image/*,.pdf,.dcm" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
          <input type="text" placeholder={t.searchScans} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="scan-search" />
        </div>
      </div>

      {/* Scans Grid */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <ScanLine className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
          <p className="text-[#6B7280]">{searchTerm ? t.noResults : t.noScansUploadedYet}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(scan => (
            <div key={scan.id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid={`scan-card-${scan.id}`}>
              <div className="h-36 bg-slate-100 flex items-center justify-center overflow-hidden">
                {scan.filename && /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(scan.filename) ? (
                  <img src={`${API}/scans/file/${scan.id}?token=${getToken()}`} alt={scan.description || scan.filename} className="w-full h-full object-cover" />
                ) : (
                  <Image className="w-10 h-10 text-[#6B7280]" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-1 mb-2">
                  <User className="w-3.5 h-3.5 text-[#0F4C5C]" />
                  <span className="text-sm font-semibold text-[#0F4C5C] truncate">{scan.patient_name}</span>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <span className="px-2 py-0.5 bg-[#0F4C5C]/10 text-[#0F4C5C] rounded text-xs font-semibold">{(scan.scan_type || 'xray').toUpperCase()}</span>
                  <span className="text-xs text-[#6B7280]">{new Date(scan.uploaded_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm font-medium text-[#1F2937] truncate">{scan.filename}</p>
                {scan.description && <p className="text-xs text-[#6B7280] mt-1 truncate">{scan.description}</p>}
                <div className="flex gap-2 mt-3">
                  <a href={`${API}/scans/file/${scan.id}?token=${getToken()}`} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-[#0F4C5C] text-white rounded-lg text-sm hover:bg-[#0F4C5C]/90 transition-colors" data-testid={`view-scan-${scan.id}`}>
                    <Download className="w-3.5 h-3.5" /> {t.view}
                  </a>
                  <button onClick={() => handleDelete(scan.id)} className="px-3 py-1.5 text-red-500 border border-red-200 rounded-lg text-sm hover:bg-red-50 transition-colors" data-testid={`delete-scan-${scan.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminScans;
